import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { getPageMetaInfo } from "@/utils/pageMetaInfo";
import { getCanonicalUrl } from "@/utils/canonicalUrls";

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  noindex?: boolean;
  structuredData?: object | object[];
  articleMeta?: {
    datePublished?: string;
    dateModified?: string;
    author?: string;
  };
}

export const SEO = ({
  title,
  description,
  canonical,
  noindex = false,
  structuredData,
  articleMeta
}: SEOProps) => {
  const location = useLocation();
  const baseUrl = 'https://www.omnseotools.ch';
  
  // Get canonical synchronously - no state needed
  const currentCanonical = canonical || getCanonicalUrl(location.pathname);
  
  const pageMetaInfo = getPageMetaInfo(location.pathname);
  
  const finalTitle = title || pageMetaInfo.title || "OMN SEO Tools - Professionelle SEO-Tools für Ihren Online-Erfolg";
  const finalDescription = description || pageMetaInfo.description || "Entdecken Sie OMN SEO Tools, Ihre Plattform für effektive SEO-Strategien und nachhaltige Steigerung von Sichtbarkeit und Traffic.";

  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "OMN SEO Tools",
    "url": baseUrl,
    "description": finalDescription,
    "inLanguage": "de-CH",
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${baseUrl}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  // Handle multiple structured data objects
  const structuredDataArray = structuredData 
    ? (Array.isArray(structuredData) ? structuredData : [structuredData])
    : [defaultStructuredData];

  return (
    <Helmet>
      <html lang="de" />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <link rel="canonical" href={currentCanonical} />
      
      {/* Geo-Tags für lokale Schweizer SEO */}
      <meta name="geo.region" content="CH" />
      <meta name="geo.placename" content="Schweiz" />
      <meta name="content-language" content="de-CH" />
      
      <meta 
        name="robots" 
        content={noindex ? "noindex,nofollow" : "index,follow"} 
      />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={currentCanonical} />
      <meta property="og:image" content={`${baseUrl}/og-image.png`} />
      <meta property="og:locale" content="de_CH" />
      <meta property="og:site_name" content="OMN SEO Tools" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={`${baseUrl}/og-image.png`} />

      {/* Article Meta (für Knowledge-Seiten) */}
      {articleMeta?.datePublished && (
        <meta property="article:published_time" content={articleMeta.datePublished} />
      )}
      {articleMeta?.dateModified && (
        <meta property="article:modified_time" content={articleMeta.dateModified} />
      )}
      {articleMeta?.author && (
        <meta property="article:author" content={articleMeta.author} />
      )}

      <meta name="author" content="OMN SEO Tools" />
      <meta name="publisher" content="OMN SEO Tools" />
      
      {/* Structured Data - Multiple schemas supported */}
      {structuredDataArray.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};